import React, { useEffect, useMemo } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import { Calendar, ClockEight } from '../../../assets/icons'
import DatePickerIconInput from '../../ui/input/datePickerIconInput'
import { addMonthsPreservingDay, createRangeDayClassName, timePopperModifiers } from './utils'
import { DatePickerWrapper, Group, InlineErrorText, Label, TimeRangeRow } from './styled'

registerLocale('es', es)

const toMinutes = date => {
	if (!date) return null
	const parsedDate = new Date(date)
	if (Number.isNaN(parsedDate.getTime())) return null
	return parsedDate.getHours() * 60 + parsedDate.getMinutes()
}

const DefaultInlineError = ({ message }) =>
	message ? <InlineErrorText>{message}</InlineErrorText> : null

const DATE_RANGE_MAX_MONTH_MESSAGE = 'El rango de fechas no puede superar los 2 meses'

const DateTimeRangeFilters = ({
	values,
	setFieldValue,
	errors = {},
	InlineErrorComponent = DefaultInlineError,
	startDateField = 'dateFrom',
	endDateField = 'dateTo',
	hourFromField = 'hourFrom',
	hourToField = 'hourTo',
	minDate,
	maxDate,
	minHourMinutes,
	maxHourMinutes,
	allowedDates = [],
	allowedHourMinutes = null,
	timeIntervals = 30,
	dateLabel = 'Fecha del turno',
	timeLabel = 'Rango horario',
	textAlign = 'left'
}) => {
	const InlineError = InlineErrorComponent
	const startDateValue = values[startDateField]
	const endDateValue = values[endDateField]
	const hourFromValue = values[hourFromField]
	const hourToValue = values[hourToField]
	const getRangeDayClassName = createRangeDayClassName(startDateValue, endDateValue)
	const exceedsMaxRange = useMemo(() => {
		if (!startDateValue || !endDateValue) return false
		const maxEndDate = addMonthsPreservingDay(startDateValue, 2)
		return maxEndDate ? new Date(endDateValue) > maxEndDate : false
	}, [startDateValue, endDateValue])
	const allowedDateSet = useMemo(() => {
		if (!Array.isArray(allowedDates) || allowedDates.length === 0) return null

		const set = new Set()
		allowedDates.forEach(date => {
			if (!date) return
			const parsed = new Date(date)
			if (Number.isNaN(parsed.getTime())) return
			set.add(parsed.setHours(0, 0, 0, 0))
		})

		return set.size > 0 ? set : null
	}, [allowedDates])
	const earliestAllowedDate = useMemo(() => {
		if (!allowedDateSet) return null
		const minMs = Math.min(...allowedDateSet)
		return Number.isFinite(minMs) ? new Date(minMs) : null
	}, [allowedDateSet])
	const allowedHourSet = useMemo(() => {
		if (!Array.isArray(allowedHourMinutes)) return null

		const set = new Set(
			allowedHourMinutes
				.map(value => Number(value))
				.filter(value => Number.isFinite(value) && value >= 0 && value < 24 * 60)
		)

		return set.size > 0 ? set : null
	}, [allowedHourMinutes])
	const isAllowedDateByDataset = date => {
		if (!allowedDateSet) return true
		const normalizedDate = new Date(date).setHours(0, 0, 0, 0)
		return allowedDateSet.has(normalizedDate)
	}
	const isAllowedHourFrom = time => {
		const optionMinutes = toMinutes(time)
		if (optionMinutes == null) return true

		if (minHourMinutes != null && optionMinutes < minHourMinutes) return false
		if (maxHourMinutes != null && optionMinutes > maxHourMinutes) return false

		if (!allowedHourSet) return true
		return allowedHourSet.has(optionMinutes)
	}
	const isAllowedHourTo = time => {
		const optionMinutes = toMinutes(time)
		if (optionMinutes == null) return true

		if (minHourMinutes != null && optionMinutes < minHourMinutes) return false
		if (maxHourMinutes != null && optionMinutes > maxHourMinutes) return false

		if (allowedHourSet && !allowedHourSet.has(optionMinutes)) return false
		if (!hourFromValue) return true
		const hourFromMinutes = toMinutes(hourFromValue)
		if (hourFromMinutes == null) return true
		return optionMinutes >= hourFromMinutes
	}

	const isWithinHourRange = minutes => {
		if (minutes == null) return true
		if (minHourMinutes != null && minutes < minHourMinutes) return false
		if (maxHourMinutes != null && minutes > maxHourMinutes) return false
		return true
	}

	useEffect(() => {
		if (!hourFromValue) return

		const hourFromMinutes = toMinutes(hourFromValue)
		const isOutsideHourRange = !isWithinHourRange(hourFromMinutes)
		const isOutsideAllowedSet = allowedHourSet && !allowedHourSet.has(hourFromMinutes)

		if (hourFromMinutes != null && (isOutsideHourRange || isOutsideAllowedSet)) {
			setFieldValue(hourFromField, null)
		}
	}, [hourFromValue, hourFromField, allowedHourSet, minHourMinutes, maxHourMinutes, setFieldValue])

	useEffect(() => {
		if (!hourToValue) return

		const hourToMinutes = toMinutes(hourToValue)
		const isOutsideHourRange = !isWithinHourRange(hourToMinutes)
		const isOutsideAllowedSet = allowedHourSet && !allowedHourSet.has(hourToMinutes)

		if (hourToMinutes != null && (isOutsideHourRange || isOutsideAllowedSet)) {
			setFieldValue(hourToField, null)
		}
	}, [hourToValue, hourToField, allowedHourSet, minHourMinutes, maxHourMinutes, setFieldValue])

	useEffect(() => {
		if (!hourFromValue || !hourToValue) return

		const hourFromMinutes = toMinutes(hourFromValue)
		const hourToMinutes = toMinutes(hourToValue)

		if (hourFromMinutes != null && hourToMinutes != null && hourToMinutes < hourFromMinutes) {
			setFieldValue(hourToField, null)
		}
	}, [hourFromValue, hourToValue, hourToField, setFieldValue])

	return (
		<>
			<Group>
				<Label $textAlign={textAlign}>{dateLabel}</Label>
				<DatePickerWrapper>
					<DatePicker
						selectsRange
						startDate={startDateValue}
						endDate={endDateValue}
						onChange={dates => {
							const [from, to] = dates

							setFieldValue(startDateField, from)
							setFieldValue(endDateField, to)
						}}
						dayClassName={getRangeDayClassName}
						filterDate={isAllowedDateByDataset}
						openToDate={startDateValue || earliestAllowedDate || minDate || undefined}
						placeholderText="Seleccioná una fecha"
						locale="es"
						dateFormat="dd/MM/yyyy"
						minDate={minDate}
						maxDate={maxDate}
						customInput={<DatePickerIconInput icon={<Calendar />} />}
						isClearable
					/>
				</DatePickerWrapper>
				<InlineError
					message={errors.dateRange || (exceedsMaxRange ? DATE_RANGE_MAX_MONTH_MESSAGE : undefined)}
				/>
			</Group>

			<Group>
				<Label $textAlign={textAlign}>{timeLabel}</Label>
				<DatePickerWrapper>
					<TimeRangeRow>
						<DatePicker
							popperClassName="time-range-popper"
							calendarClassName="time-range-calendar"
							popperModifiers={timePopperModifiers}
							selected={hourFromValue}
							onChange={date => setFieldValue(hourFromField, date)}
							showTimeSelect
							showTimeSelectOnly
							timeIntervals={timeIntervals}
							filterTime={isAllowedHourFrom}
							timeCaption="Desde"
							dateFormat="HH:mm"
							timeFormat="HH:mm"
							placeholderText="00:00"
							customInput={
								<DatePickerIconInput icon={<ClockEight />} inputClassName="time-range-input" />
							}
							isClearable
						/>
						<DatePicker
							popperClassName="time-range-popper"
							calendarClassName="time-range-calendar"
							popperModifiers={timePopperModifiers}
							selected={hourToValue}
							onChange={date => setFieldValue(hourToField, date)}
							showTimeSelect
							showTimeSelectOnly
							timeIntervals={timeIntervals}
							filterTime={isAllowedHourTo}
							timeCaption="Hasta"
							dateFormat="HH:mm"
							timeFormat="HH:mm"
							placeholderText="00:00"
							customInput={
								<DatePickerIconInput icon={<ClockEight />} inputClassName="time-range-input" />
							}
							isClearable
						/>
					</TimeRangeRow>
				</DatePickerWrapper>
				<InlineError message={errors.timeRange} />
			</Group>
		</>
	)
}

export default DateTimeRangeFilters
